import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { generatePrescriptionPDF } from "../services/generatePDF";
import {
  serviceFetchPatientDetails,
  serviceSendPrescriptionDataToBackend,
  serviceFetchDoctorDetails,
} from "../services/api";
import {
  fetchClinicAddressDetails,
  fetchClinicBasicDetails,
} from "../../clinic/services/billingApi";
import { debounce, set } from "lodash";
import MedicationsList from "../components/MedicationsList";
import UserDetails from "../components/UserDetails";
import StickyHeader from "../components/StickyHeader";
import Vitals from "../components/Vitals";
import SymptomsInput from "../components/SymptomsInput";
import InvestigationsInput from "../components/InvestigationsInput";
import {
  getClinicIdFromToken,
  getToken,
  getValueFromToken,
} from "../../clinic/utils/tokenUtils";
import { serviceGetClinicAddressDetails } from "../../clinic/services/profileApi";
import {
  CustomInput,
  calculateAgeR,
  handleResponseErrorData,
} from "../../clinic/utils/commonUtils";
import { showToast } from "../../patient/utils/toastUtils";

import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getPatientDetailsWithContactInfo } from "../../clinic/services/api";

// @atif can create a css/customise tailwind later

const Prescription = () => {
  const navigate = useNavigate();
  const [redirectUrl, setRedirectUrl] = useState("");
  const [patient, setPatient] = useState(""); // This will hold the selected patient
  const [labTestSuggestions, setLabTestSuggestions] = useState([]);
  const [medications, setMedications] = useState([
    {
      medicine: "",
      medication_id: null,
      dosage: "",
      frequency: "",
      duration: "",
      route: "",
      notes: "",
    },
  ]);
  const [vitals, setVitals] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [appointmentId, setAppointmentId] = useState(null);
  const [doctorid, setDoctorid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [doctorData, setDoctorData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [clinicDetails, setClinicDetails] = useState({});
  const [patientDetails, setPatientDetails] = useState({
    name: "",
    age: "",
    gender: "",
    phone: "",
    // Add other fields as needed
  });
  const [symptoms, setSymptoms] = useState([]);
  const [investigations, setInvestigations] = useState([]);

  const location = useLocation();
  const medicationsRef = useRef(null);
  const userDetailsRef = useRef(null);
  const vitalsRef = useRef(null);
  const symptomsRef = useRef(null);
  const investigationsRef = useRef(null);

  const scrollToRef = (ref) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const updatePreview = useCallback(
    debounce((formData) => {
      setPreviewData(formData);
    }, 1000),
    []
  );

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const preSelectPatient = async (patientId) => {
    const response = await getPatientDetailsWithContactInfo(patientId);
    if (response?.patient) {
      const patientData = response.patient;
      if (patientData && patientData.userDetails) {
        const { first_name, last_name, gender, date_of_birth } =
          patientData.userDetails;
        setPatientDetails({
          name: `${first_name} ${last_name}`,
          age: calculateAgeR(date_of_birth),
          gender: gender,
          phone: response.primary_phone_number,
          patient_id: patientData.patient_id,
        });
      }
    } else {
      handleResponseErrorData(response, null);
    }
  };

  const addMedication = () => {
    setMedications((prevMedications) => [
      ...prevMedications,
      {
        medicine: "",
        medication_id: null,
        dosage: "",
        frequency: "",
        duration: "",
        route: "",
        notes: "",
      },
    ]);
  };

  const generatePreview = async (formData) => {
    try {
      const pdfBlob = await generatePrescriptionPDF(
        formData.patientDetails,
        formData.medications,
        formData.vitals,
        formData.symptoms,
        formData.investigations,
        doctorData,
        clinicDetails,
        formData.followUpDate
      );
      const objectURL = URL.createObjectURL(pdfBlob);
      setPreviewUrl(objectURL);
    } catch (error) {
      console.error("Error generating prescription preview:", error);
    }
  };

  // Debounced function to update the preview data
  const debouncedGeneratePreview = useCallback(
    debounce(generatePreview, 1000),
    []
  );

  // Effect for updating preview when form data changes
  useEffect(() => {
    if (patient && doctorData && clinicDetails) {
      debouncedGeneratePreview({
        patientDetails,
        medications,
        vitals,
        symptoms,
        investigations,
        followUpDate,
      });
    }

    return () => debouncedGeneratePreview.cancel();
  }, [
    patient,
    medications,
    vitals,
    symptoms,
    investigations,
    followUpDate,
    doctorData,
    clinicDetails,
    debouncedGeneratePreview,
  ]);
  const [activeTab, setActiveTab] = useState(userDetailsRef);

  useEffect(() => {
    // Extract the patientId from URL search parameters
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patientId");
    const getAppointmentId = searchParams.get("appointmentId");
    const getDoctorId = searchParams.get("doctorId");
    const getRedirectUrl = searchParams.get("redirect");
    updateRedirectUrl(getRedirectUrl);
    setDoctorid(getDoctorId);
    setAppointmentId(getAppointmentId);
    console.log("set data-->", doctorid, appointmentId);

    if (patientId) {
      preSelectPatient(patientId);
    }
  }, [location]);

  const updateRedirectUrl = (url) => {
    if (url) {
      setRedirectUrl(url);
    } else {
      setRedirectUrl("clinic/dashboard");
    }
  };

  const fetchDoctorData = async () => {
    const userId = getValueFromToken("user_id");
    try {
      const doctorId = doctorid;
      const response = await serviceFetchDoctorDetails(doctorId);
      if (response?.status) {
        setDoctorData(response?.data);
        const addressDetails = await serviceGetClinicAddressDetails({ userId });
        const clinicId = getClinicIdFromToken();
        const clinicDetails = await fetchClinicBasicDetails(clinicId);
        const addressParts = addressDetails
          ? [
              addressDetails.street_1,
              addressDetails.street_2,
              addressDetails.city,
              addressDetails.state,
              addressDetails.country,
              addressDetails.pin_code,
            ].filter(Boolean)
          : [];
        const clinicAddress = `${clinicDetails}${
          addressParts.length > 0 ? ", " + addressParts.join(", ") : ""
        }`;
        setClinicDetails({
          address: clinicAddress,
          name: `${clinicDetails}`,
        });
      } else {
        throw response;
      }
    } catch (error) {
      handleResponseErrorData(error, null);
    }
  };

  useEffect(() => {
    if (doctorid) {
      fetchDoctorData();
    }
  }, [doctorid]);

  const collectAndSendPrescriptionData = async (pdfBlob) => {
    const prescriptionData = {
      patient_id: patientDetails.patient_id, // Correct usage of patient_id
      patientDetail: patientDetails,
      doctor_id: doctorid, //hardcoded for now
      appointment_id: appointmentId, //hardcoded for now
      clinic_id: getClinicIdFromToken(), //hardcoded for now
      medications: medications.map((med) => ({
        medicine:
          typeof med.medicine === "object"
            ? med.medicine.full_name
            : med.medicine,
        medication_id: med.medication_id,
        dosage: med.dosage,
        frequency: med.frequency,
        duration: med.duration,
        route: med.route,
        notes: med.notes,
      })),
      vitals: vitals.map((v) => ({
        vital_id: v.vital_id,
        value: v.value,
        // Units are not required to send to backend unless specified.
      })),
      symptoms: symptoms.map((symptom) => symptom.value),
      investigations: investigations.map(
        (investigation) => investigation.value
      ),
      doctorData: doctorData,
      clinicDetails: clinicDetails,
      followUpDate: followUpDate,
    };

    const formData = new FormData();
    formData.append("pdf", pdfBlob, "prescription.pdf");

    formData.append("prescriptionData", JSON.stringify(prescriptionData));
    console.log("ye hai formData...: ", formData);
    try {
      const response = await serviceSendPrescriptionDataToBackend(formData);
      if (response?.status) {
        showToast("Prescription saved successfully", "success");
        // go back to the previous page
        navigate(`/${redirectUrl}`);
      } else {
        handleResponseErrorData(response, null);
      }
    } catch (error) {
      console.error("Error sending prescription data to backend:", error);
    }
  };

  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
    const day = currentDate.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    console.log("generatePrescriptionPDF", patientDetails);
    setPatient(patientDetails.name);
  }, [patientDetails]);

  const handlePreview = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmissionError("");

    const pdfBlob = await generatePrescriptionPDF(
      patientDetails,
      medications,
      vitals,
      symptoms,
      investigations,
      doctorData,
      clinicDetails,
      followUpDate
    );
    const objectURL = URL.createObjectURL(pdfBlob);
    setPreviewUrl(objectURL);
    setIsPreviewOpen(true);
    setIsSubmitting(false);
  };
  const handleSubmit = async (event) => {
    console.log("submit button clicked");
    event.preventDefault();
    setIsSubmitting(true);
    setSubmissionError("");
    if (patient === null || patient === "") {
      setPatientDetails({
        name: null,
        age: null,
        gender: null,
        phone: null,
        patient_id: null,
      });
      showToast("Please select a patient before submitting.", "error");
      return;
    }

    try {
      const pdfBlob = await generatePrescriptionPDF(
        patientDetails,
        medications,
        vitals,
        symptoms,
        investigations,
        doctorData,
        clinicDetails,
        followUpDate
      );
      const objectURL = URL.createObjectURL(pdfBlob);
      setPreviewUrl(objectURL);
      console.log("reached here.....");
      await collectAndSendPrescriptionData(pdfBlob);

      setMedications([
        {
          medicine: "",
          medication_id: null,
          dosage: "",
          frequency: "",
          duration: "",
          route: "",
          notes: "",
        },
      ]);
      setPatient("");
      setFollowUpDate("");
    } catch (error) {
      console.error("Error submitting prescription:", error);
      setSubmissionError("Failed to submit prescription. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-row">
      <div className="w-1/2">
        <div className="min-h-screen flex flex-col items-center justify-start pt-10 px-4">
          <div ref={userDetailsRef} className="my-6 relative z-30">
            <div className="w-full max-w-4xl px-3">
              {/* Pass both patient and setPatient to UserDetails */}
              <UserDetails
                patient={patient}
                setPatient={setPatient}
                patientDetails={patientDetails}
                setPatientDetails={setPatientDetails}
              />
            </div>
          </div>

          {/* StickyHeader comes after search */}
          <StickyHeader
            scrollToRef={scrollToRef}
            userDetailsRef={userDetailsRef}
            medicationsRef={medicationsRef}
            vitalsRef={vitalsRef}
            symptomsRef={symptomsRef}
            investigationsRef={investigationsRef}
            active={activeTab}
            setActive={setActiveTab}
          />

          <form onSubmit={handleSubmit} className="w-full max-w-4xl">
            <div className="rounded shadow-xl overflow-hidden max-w-4xl w-full bg-white">
              <div className="p-10">
                {/* Symptoms Section */}
                <div ref={symptomsRef} className="mb-6 pt-6">
                  <SymptomsInput
                    symptoms={symptoms}
                    setSymptoms={setSymptoms}
                    doctorId={1}
                    setLabTestSuggestions={setLabTestSuggestions} // Pass the setter function
                  />
                </div>

                <div ref={vitalsRef} className="mb-6 pt-6">
                  <Vitals vitals={vitals} setVitals={setVitals} />
                </div>

                {/* Medications Section */}
                <div ref={medicationsRef} className="mb-6">
                  <MedicationsList
                    medications={medications}
                    setMedications={setMedications}
                    addMedication={addMedication}
                    // pass down other necessary props like medicationTypes etc.
                  />
                </div>

                {/* Vitals Section */}

                {/* Investigations Section */}
                <div ref={investigationsRef} className="mb-6 pt-6">
                  <InvestigationsInput
                    investigations={investigations}
                    setInvestigations={setInvestigations}
                    labTestSuggestions={labTestSuggestions} // Passing the suggestions to InvestigationsInput
                    doctorId={1} // Pass the doctor's ID as needed
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="followUpDate" className="block mb-2">
                    Follow-up Date:
                  </label>

                  <ReactDatePicker
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    selected={followUpDate}
                    id="followUpDate"
                    onChange={(date) => setFollowUpDate(date)}
                    minDate={new Date()}
                    className="input-control"
                    dateFormat="dd-MM-yyyy"
                    customInput={<CustomInput />}
                  />
                </div>
                {/* Submit Button */}
                <div className="mt-4">
                  <button
                    type="submit"
                    className="text-green-700 bg-green-100 hover:bg-green-200 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:focus:ring-green-900 transition-colors"
                  >
                    Save Prescription
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Preview Section */}
      <div className="w-1/2">
        {previewUrl && (
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="Prescription Preview"
          ></iframe>
        )}
        {!previewUrl && (
          <div className=" flex text-center fixed w-[calc(50%-32px)] right-0 top-0 z-0 h-full justify-center  items-center  bg-gray-200">
            <div>
              Not record found <br />
              Please select your patient
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prescription;
